<template>
  <component
    v-bind="bindings"
    :is="is"
    ref="$el"
    :class="cmpClass"
    :style="cmpStyle"
    @click="e => handleClick(e)"
    @mousedown="e => handleMousedown(e)"
    @mouseenter="e => handleMouseenter(e)"
    @mouseleave="e => handleMouseleave(e)"
    @mouseup="e => handleMouseup(e)"
  >
    <template
      v-if="(iconPosition === 'left' && (loading || icon)) || (!iconPosition && icon)"
    >
      <Icon
        v-if="loading"
        class="icon-left"
        :style="iconSpace && { marginRight: iconSpace }"
        :icon="LoadingFour"
        color="primary"
        spin
      />
      <Icon
        v-else
        class="icon-left"
        :style="iconSpace && { marginRight: iconSpace }"
        v-bind="iconProps"
      />
    </template>
    <slot />
    <template
      v-if="(iconPosition === 'right' && (loading || icon)) || (!iconPosition && !icon && loading)"
    >
      <Icon
        v-if="loading"
        class="icon-right"
        :style="iconSpace && { marginLeft: iconSpace }"
        :icon="LoadingFour"
        color="primary"
        spin
      />
      <Icon
        v-else
        class="icon-right"
        :style="iconSpace && { marginLeft: iconSpace }"
        v-bind="iconProps"
      />
    </template>
    <Tooltip
      v-if="$el && tooltip"
      :target="$el"
      :validate="() => (typeof tooltip === 'string' || isEllipsisActive())"
    >
      <template #content>
        <template v-if="typeof tooltip === 'string'">{{ tooltip }}</template>
        <slot v-else />
      </template>
    </Tooltip>
  </component>
</template>
<script setup lang="ts">
  import {
    ref, reactive, computed, watch, watchEffect, withDefaults,
  } from 'vue'
  import { Tooltip } from '../Popover'
  import { Icon, IconProps } from '../Icon'
  import { LoadingFour } from '../../icons'
  import { IconContent } from '../Icon/interface'
  import { AnchorAttrs } from './interface'
  import {
    TextColor, TextType, TextSize, ButtonIconPosition,
  } from '../../enums'
  import { MouseState } from '../../states'

  const props = withDefaults(
    defineProps<{
      type?: TextType
      size?: TextSize
      color?: TextColor
      icon?:((p: IconProps) => string) | Partial<IconContent>
      iconPosition?: ButtonIconPosition
      iconSpace?: string | number
      bold?: boolean
      heavy?: boolean
      ellipsis?: boolean
      tooltip?: boolean | string
      maxLines?: number
      link?: boolean | Partial<AnchorAttrs>
      monospace?: boolean
      disabled?: boolean
      loading?: boolean
    }>(),
    {
      type: 'paragraph',
      icon: undefined,
      iconPosition: undefined,
      iconSpace: undefined,
      size: undefined,
      bold: false,
      heavy: false,
      color: undefined,
      ellipsis: false,
      tooltip: undefined,
      maxLines: undefined,
      link: undefined,
      monospace: false,
      disabled: false,
      loading: false,
    },
  )

  const emits = defineEmits<{(e: 'click', event: MouseEvent): void; (e: 'mousedown', event: MouseEvent): void; (e: 'mouseenter', event: MouseEvent): void; (e: 'mouseleave', event: MouseEvent): void; (e: 'mouseup', event: MouseEvent): void; (e: 'state-change', state: MouseState): void}>()

  const state = reactive<MouseState>({
    pressing: false,
    hover: false,
  })

  watch(
    state,
    v => emits('state-change', v),
  )

  const iconProps = computed(
    () => {
      let size: string | number = props.size

      switch (props.type) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
          size = 'extra-large'
          break
        case 'h6':
          size = 'large'
          break
        default:
          break
      }

      return {
        size,
        ...typeof props.icon === 'function'
          ? { icon: props.icon }
          : props.icon,
      }
    },
  )

  const iconSpace = computed(
    () => props.iconSpace && (
      typeof props.iconSpace === 'number'
        ? `${props.iconSpace}px`
        : props.iconSpace
    ),
  )

  const cmpClass = computed(
    () => [
      'd-text',
      !props.link && (
        (props.disabled && '--color-text-disabled')
        || (props.color && `--color-${props.color}`)
        || `--color-text-${props.type}`
      ),
      (props.size && `--size-text-${props.size}`)
        || (props.type && `--size-text-${props.type}`),
      props.bold && 'bold',
      props.heavy && 'heavy',
      ...props.link
        ? ['d-focusable', '--color-primary']
        : [],
      ((props.ellipsis && props.maxLines === undefined) || props.maxLines === 1) && 'd-text-nowrap',
      props.ellipsis && 'd-text-ellipsis',
      props.maxLines > 1 && 'd-text-multi-lines',
      props.monospace && 'd-text-monospace',
      props.disabled && 'disabled',
      state.pressing && 'pressing',
    ],
  )

  const cmpStyle = computed(
    () => (
      props.maxLines > 1 && {
        '-webkit-line-clamp': props.maxLines,
      }
    ),
  )

  const bindings = computed(
    () => {
      if (props.link && !props.disabled) {
        return props.link
      }
      return undefined
    },
  )

  const is = computed(
    () => {
      if (props.link) {
        return 'a'
      }
      if (['paragraph', 'description'].includes(props.type)) {
        return 'span'
      }
      return props.type
    },
  )

  function handleClick(e: MouseEvent) {
    emits('click', e)
  }

  function handleMousedown(e: MouseEvent) {
    emits('mousedown', e)
    state.pressing = true
  }

  function handleMouseenter(e: MouseEvent) {
    emits('mouseenter', e)
    state.hover = true
  }

  function handleMouseleave(e: MouseEvent) {
    emits('mouseleave', e)
    state.pressing = false
    state.hover = false
  }

  function handleMouseup(e: MouseEvent) {
    emits('mouseup', e)
    state.pressing = false
  }

  const $el = ref<HTMLElement>()

  function isEllipsisActive() {
    return !!$el.value && ($el.value.clientWidth < $el.value.scrollWidth)
  }

  const exposed = reactive({
    $el: $el.value,
    isEllipsisActive,
  })

  watchEffect(
    () => {
      exposed.$el = $el.value
    },
  )

  defineExpose(exposed)
</script>
